import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Image } from 'lucide-react';
import { apiCache } from '../utils/apiCache';

const EditSongModal = ({ song, vibes, onClose, onSave }) => {
  console.log('🎵 EditSongModal - Opening with song:', song.title);
  console.log('🏷️ EditSongModal - Song tags:', song.tags);
  console.log('🏷️ EditSongModal - Tags is array?', Array.isArray(song.tags));
  console.log('🏷️ EditSongModal - Tags value:', JSON.stringify(song.tags));
  
  const [formData, setFormData] = useState({
    title: song.title || '',
    artist: song.artist || '',
    album: song.album || '',
    genre: song.genre || '',
    vibes: song.vibes || [],
    tags: song.tags || [],
    duration: song.duration || 0,
    albumArt: song.albumArt || ''
  });
  
  console.log('📝 EditSongModal - Initial formData.tags:', JSON.stringify(formData.tags));
  
  const [albumArtFile, setAlbumArtFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [genres, setGenres] = useState([]);
  const [predefinedTags, setPredefinedTags] = useState([]);
  
  // Ref to file input for resetting
  const fileInputRef = useRef(null);

  // Sync formData with song prop whenever it changes
  useEffect(() => {
    console.log('🔄 EditSongModal - Song prop changed, syncing formData');
    console.log('🏷️ New song.tags:', JSON.stringify(song.tags));
    setFormData({
      title: song.title || '',
      artist: song.artist || '',
      album: song.album || '',
      genre: song.genre || '',
      vibes: song.vibes || [],
      tags: song.tags || [],
      duration: song.duration || 0,
      albumArt: song.albumArt || ''
    });
    // Reset album art file and file input when song changes
    setAlbumArtFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [song]);

  // Fetch genres and tags on mount
  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/genres`);
        if (response.ok) {
          const data = await response.json();
          setGenres(data);
        }
      } catch (error) {
        console.error('Failed to fetch genres:', error);
      }
    };
    
    const fetchTags = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tags`);
        if (response.ok) {
          const data = await response.json();
          setPredefinedTags(data.map(tag => tag.name));
        }
      } catch (error) {
        console.error('Failed to fetch tags:', error);
      }
    };
    
    fetchGenres();
    fetchTags();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleVibeToggle = (vibeId) => {
    setFormData(prev => ({
      ...prev,
      vibes: prev.vibes.includes(vibeId) 
        ? prev.vibes.filter(id => id !== vibeId)
        : [...prev.vibes, vibeId]
    }));
  };

  const handleTagToggle = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) 
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const handleAlbumArtChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setAlbumArtFile(file);
        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        setFormData(prev => ({ ...prev, albumArt: previewUrl }));
      } else {
        setMessage('Please select an image file');
      }
    }
  };

  const uploadAlbumArt = async () => {
    if (!albumArtFile) return null;
    
    console.log('📤 Starting album art upload for song:', song.id);
    console.log('📤 File details:', {
      name: albumArtFile.name,
      type: albumArtFile.type,
      size: albumArtFile.size
    });
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', albumArtFile);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/songs/${song.id}/album-art`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: formDataUpload
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Upload failed:', response.status, errorData);
        throw new Error(errorData.detail || 'Upload failed');
      }
      
      const result = await response.json();
      console.log('✅ Upload successful:', result.album_art_url);
      
      // Reset file input after successful upload
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setAlbumArtFile(null);
      
      return result.album_art_url;
    } catch (error) {
      console.error('❌ Upload error:', error);
      throw new Error(error.message || 'Failed to upload album art');
    }
  };

  const handleSave = async () => {
    setUploading(true);
    setMessage('');
    
    try {
      let albumArtUrl = formData.albumArt;
      
      // Upload new album art if selected
      if (albumArtFile) {
        setMessage('Uploading album art...');
        albumArtUrl = await uploadAlbumArt();
        
        // Add cache-busting parameter to force image refresh
        if (albumArtUrl && !albumArtUrl.includes('?')) {
          albumArtUrl = `${albumArtUrl}?t=${Date.now()}`;
        }
      }
      
      // Update song details via backend API
      setMessage('Updating song details...');
      const updatePayload = {
        title: formData.title,
        artist: formData.artist,
        album: formData.album,
        genre: formData.genre,
        vibes: formData.vibes,
        tags: formData.tags,
        duration: formData.duration,
        album_art_url: albumArtUrl
      };
      console.log('📤 Sending update with tags:', updatePayload.tags);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/songs/${song.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify(updatePayload)
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update song');
      }

      const updatedSong = await response.json();
      console.log('✅ Backend returned updated song with tags:', updatedSong.tags);
      
      // Invalidate songs cache to force refresh
      apiCache.invalidate('songs');
      console.log('🗑️ Invalidated songs cache');
      
      // Update formData to show new album art immediately
      setFormData(prev => ({
        ...prev,
        albumArt: updatedSong.album_art_url || updatedSong.albumArt
      }));
      
      setMessage('Song updated successfully! Refreshing...');
      
      // Call onSave callback with updated song
      // The parent will handle refreshing the songs list
      await onSave({
        ...updatedSong,
        albumArt: updatedSong.album_art_url,
        audioUrl: updatedSong.audio_url
      });
      
      // Close modal after a brief delay to show success message
      setTimeout(() => {
        onClose();
      }, 1000);
      
    } catch (error) {
      console.error('❌ Save error:', error);
      setMessage(error.message || 'Failed to save changes');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content edit-song-modal">
        <div className="modal-header">
          <h3>Edit Song: {song.title}</h3>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {/* Album Art Section */}
          <div className="form-group album-art-section">
            <label>Album Art</label>
            <div className="album-art-upload">
              <div className="current-art">
                <img 
                  src={formData.albumArt} 
                  alt="Album Art"
                  className="album-art-preview"
                />
              </div>
              <div className="upload-controls">
                <input
                  ref={fileInputRef}
                  type="file"
                  id="album-art-input"
                  accept="image/*"
                  onChange={handleAlbumArtChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="album-art-input" className="upload-button">
                  <Upload size={16} />
                  Choose New Image
                </label>
                <div className="upload-hint">
                  JPG, PNG, GIF up to 5MB
                </div>
              </div>
            </div>
          </div>

          {/* Song Details */}
          <div className="form-row">
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Artist</label>
              <input
                type="text"
                value={formData.artist}
                onChange={(e) => handleInputChange('artist', e.target.value)}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Album</label>
              <input
                type="text"
                value={formData.album}
                onChange={(e) => handleInputChange('album', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Genre</label>
              <select
                value={formData.genre}
                onChange={(e) => handleInputChange('genre', e.target.value)}
                className="form-input"
              >
                <option value="">Select Genre</option>
                {genres.map(genre => (
                  <option key={genre.id} value={genre.slug}>
                    {genre.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Vibes Selection */}
          <div className="form-group">
            <label>Vibes</label>
            <div className="vibes-selection">
              {vibes.map(vibe => (
                <label key={vibe.id} className="vibe-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.vibes.includes(vibe.id)}
                    onChange={() => handleVibeToggle(vibe.id)}
                  />
                  <span className="vibe-name">{vibe.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Tags Selection for Recommendations */}
          <div className="form-group">
            <label>
              Tags for Recommendations
              <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>
                (Select tags to improve "Made For You" recommendations)
              </span>
            </label>
            <div className="tags-selection" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: '8px',
              marginTop: '8px'
            }}>
              {predefinedTags.map(tag => {
                const isChecked = formData.tags.includes(tag);
                console.log(`🏷️ Rendering tag "${tag}": checked=${isChecked}, formData.tags=`, formData.tags);
                return (
                  <label 
                    key={tag} 
                    className="tag-checkbox"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 12px',
                      border: `2px solid ${isChecked ? '#9D4EDD' : '#ddd'}`,
                      borderRadius: '20px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      background: isChecked ? '#f3e8ff' : 'white',
                      transition: 'all 0.2s',
                      color: '#333'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleTagToggle(tag)}
                      style={{ marginRight: '6px' }}
                    />
                    <span style={{ color: '#333' }}>{tag}</span>
                  </label>
                );
              })}
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '8px',
              fontStyle: 'italic'
            }}>
              Selected: {formData.tags.length > 0 ? formData.tags.join(', ') : 'None'}
            </div>
          </div>

          {message && (
            <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="button primary"
            onClick={handleSave}
            disabled={uploading}
          >
            {uploading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditSongModal;